// src/auth/GuestDashboardOverlay.jsx
import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useAuth0 } from '@auth0/auth0-react';

// ============================================
// ANIMATIONS
// ============================================

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { 
    opacity: 0; 
    transform: translateY(30px);
  }
  to { 
    opacity: 1; 
    transform: translateY(0);
  }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 0.6; }
`;

// ============================================
// STYLED COMPONENTS
// ============================================

const LandingContainer = styled.div`
  min-height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0a0c14 0%, #141824 50%, #1a1f2e 100%);
  position: relative;
  overflow: hidden;
  animation: ${fadeIn} 0.4s ease-out;
`;

// Decorative background elements
const BackgroundDecoration = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  overflow: hidden;
`;

const GlowOrb = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  animation: ${pulse} 4s ease-in-out infinite;
  
  &.orb1 {
    width: 400px;
    height: 400px;
    background: radial-gradient(circle, rgba(99, 102, 241, 0.3) 0%, transparent 70%);
    top: -100px;
    right: -100px;
    animation-delay: 0s;
  }
  
  &.orb2 {
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(139, 92, 246, 0.25) 0%, transparent 70%);
    bottom: -50px;
    left: -50px;
    animation-delay: 2s;
  }
  
  &.orb3 {
    width: 200px;
    height: 200px;
    background: radial-gradient(circle, rgba(99, 102, 241, 0.2) 0%, transparent 70%);
    top: 40%;
    left: 20%;
    animation-delay: 1s;
  }
`;

const GridPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    linear-gradient(rgba(99, 102, 241, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(99, 102, 241, 0.03) 1px, transparent 1px);
  background-size: 50px 50px;
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 20px;
  max-width: 600px;
  width: 100%;
`;

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 32px;
  animation: ${float} 6s ease-in-out infinite;
`;

const Logo = styled.div`
  width: 88px;
  height: 88px;
  border-radius: 22px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a78bfa 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 
    0 0 60px rgba(99, 102, 241, 0.4),
    0 20px 40px rgba(0, 0, 0, 0.3);
  
  svg {
    width: 48px;
    height: 48px;
    color: white;
  }
`;

const Title = styled.h1`
  font-family: 'Space Grotesk', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 42px;
  font-weight: 700;
  text-align: center;
  margin: 0 0 16px;
  background: linear-gradient(135deg, #ffffff 0%, #e0e7ff 50%, #a5b4fc 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${slideUp} 0.6s ease-out 0.1s both;
  
  @media (max-width: 480px) {
    font-size: 32px;
  }
`;

const Subtitle = styled.p`
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 18px;
  color: #94a3b8;
  text-align: center;
  margin: 0 0 40px;
  line-height: 1.7;
  max-width: 460px;
  animation: ${slideUp} 0.6s ease-out 0.2s both;
  
  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 380px;
  animation: ${slideUp} 0.6s ease-out 0.3s both;
`;

const PrimaryButton = styled.button`
  width: 100%;
  padding: 18px 28px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 17px;
  font-weight: 600;
  color: white;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border: none;
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 
      0 0 40px rgba(99, 102, 241, 0.5),
      0 15px 30px rgba(99, 102, 241, 0.3);
  }
  
  &:active {
    transform: translateY(-1px);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    background-size: 200% 100%;
    animation: ${shimmer} 3s infinite;
  }
`;

const SecondaryButton = styled.button`
  width: 100%;
  padding: 18px 28px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 17px;
  font-weight: 600;
  color: #c7d2fe;
  background: rgba(99, 102, 241, 0.08);
  border: 1px solid rgba(99, 102, 241, 0.25);
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    background: rgba(99, 102, 241, 0.15);
    border-color: rgba(99, 102, 241, 0.4);
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  
  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(99, 102, 241, 0.3), transparent);
  }
  
  span {
    font-family: 'Inter', sans-serif;
    font-size: 13px;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.1em;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-top: 48px;
  width: 100%;
  max-width: 480px;
  animation: ${slideUp} 0.6s ease-out 0.4s both;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: rgba(99, 102, 241, 0.05);
  border: 1px solid rgba(99, 102, 241, 0.1);
  border-radius: 12px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(99, 102, 241, 0.1);
    border-color: rgba(99, 102, 241, 0.2);
    transform: translateY(-2px);
  }
  
  svg {
    width: 20px;
    height: 20px;
    color: #8b5cf6;
    flex-shrink: 0;
  }
  
  span {
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    color: #c7d2fe;
    font-weight: 500;
  }
`;

const Footer = styled.div`
  position: absolute;
  bottom: 24px;
  left: 0;
  right: 0;
  text-align: center;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: #475569;
  animation: ${fadeIn} 0.6s ease-out 0.5s both;
  
  a {
    color: #6366f1;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

// ============================================
// COMPONENT
// ============================================

const GuestDashboardOverlay = () => {
  const { loginWithRedirect } = useAuth0();

  // Preserve the FULL deep link (path + query + hash) through the Auth0 round-trip,
  // so an email link like /network (or /referral-consent/:token?x=y) is restored
  // after login instead of dropping the user on the dashboard.
  const currentDeepLink = () =>
    window.location.pathname + window.location.search + window.location.hash;

  const handleSignUp = () => {
    loginWithRedirect({
      authorizationParams: {
        screen_hint: 'signup'
      },
      appState: {
        returnTo: currentDeepLink()
      }
    });
  };

  const handleLogin = () => {
    loginWithRedirect({
      appState: {
        returnTo: currentDeepLink()
      }
    });
  };

  return (
    <LandingContainer>
      <BackgroundDecoration>
        <GridPattern />
        <GlowOrb className="orb1" />
        <GlowOrb className="orb2" />
        <GlowOrb className="orb3" />
      </BackgroundDecoration>
      
      <ContentWrapper>
        <LogoContainer>
          <Logo>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2L2 7l10 5 10-5-10-5z" />
              <path d="M2 17l10 5 10-5" />
              <path d="M2 12l10 5 10-5" />
            </svg>
          </Logo>
        </LogoContainer>
        
        <Title>Welcome to iMaven AI</Title>
        <Subtitle>
          The intelligent platform for managing client interactions, 
          automating workflows, and scaling your business with AI.
        </Subtitle>
        
        <ButtonsContainer>
          <PrimaryButton onClick={handleSignUp}>
            Get Started — Create Account
          </PrimaryButton>
          
          <Divider>
            <span>or</span>
          </Divider>
          
          <SecondaryButton onClick={handleLogin}>
            Sign In to Your Account
          </SecondaryButton>
        </ButtonsContainer>
        
        <FeaturesGrid>
          <FeatureCard>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <span>AI-Powered Chat</span>
          </FeatureCard>
          
          <FeatureCard>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            <span>Smart Scheduling</span>
          </FeatureCard>
          
          <FeatureCard>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            <span>Client Management</span>
          </FeatureCard>
          
          <FeatureCard>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="20" x2="18" y2="10" />
              <line x1="12" y1="20" x2="12" y2="4" />
              <line x1="6" y1="20" x2="6" y2="14" />
            </svg>
            <span>Analytics & Insights</span>
          </FeatureCard>
        </FeaturesGrid>
      </ContentWrapper>
      
      <Footer>
        © 2026 iMaven AI · <a href="mailto:support@imavenai.com">Contact Support</a>
      </Footer>
    </LandingContainer>
  );
};

export default GuestDashboardOverlay;
